import {
  isDictationHotkeySettingsEnabled,
  useIsDictationHotkeySettings,
} from '@/app-shell/flags/dictate-flags';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import {
  DICTATION_HOTKEY_DEFAULTS,
  PLATFORM_HOTKEYS_FIELD,
  getKeyboardLayout,
  getUserPlatform,
  isReservedSystemHotkey,
  resolveStoredChord,
  useKeyboardLayout,
  type DictationHotkeyPlatform,
  type KeyboardLayout,
} from '@heidi/dictate';
import { isMac } from '@heidi/utils';
import { getCurrentUser } from '@heidi/workspace/data';

// Re-export the platform symbols from the leaf module so existing consumers
// keep working without each one chasing the import.
export { getUserPlatform, type DictationHotkeyPlatform };

// Re-exported so existing consumers keep working without chasing the import.
export { PLATFORM_HOTKEYS_FIELD };

/**
 * Resolves a stored chord, falling back to default when empty, unknown, or —
 * on desktop — on the current reserved list. Read-time reset so a chord that
 * becomes reserved in a newer client is silently swapped without touching the
 * user record. Web skips the reserved check.
 */
export const resolveUsableChord = (
  platform: DictationHotkeyPlatform,
  stored: string[] | null | undefined,
  defaults: Record<DictationHotkeyPlatform, readonly string[]> = DICTATION_HOTKEY_DEFAULTS
): string[] => {
  const resolved = resolveStoredChord(platform, stored, defaults);
  if (platform !== 'web' && isReservedSystemHotkey(platform, resolved)) {
    return [...defaults[platform]];
  }
  return resolved;
};

/**
 * Layout-aware web default chord. Tracks the onboarding asset split:
 * - JIS (japan) ships one image — IntlYen / ¥ for both Mac and PC users.
 * - AZERTY (france-belgium) ships split web-mac / web-pc images — Mac uses
 *   BracketRight (£), PC keeps the Backquote (²) default.
 * - Every other layout ships one shared image — Backquote for both OSes.
 * See `useDictationHotkey.test.ts` for the truth table.
 */
const resolveWebDefault = (layout: KeyboardLayout, macOs: boolean): readonly string[] => {
  switch (layout) {
    case 'japan':
      return ['IntlYen'];
    case 'france-belgium':
      return macOs ? ['BracketRight'] : DICTATION_HOTKEY_DEFAULTS.web;
    default:
      return DICTATION_HOTKEY_DEFAULTS.web;
  }
};

/**
 * Non-hook: read current dictation hotkey chord from the query cache. Layout
 * resolution piggy-backs on the cached `keyboardLayoutAtom` written by
 * `useKeyboardLayout` — so JIS/AZERTY-mac web users get their actual key
 * (IntlYen / BracketRight) instead of the layout-blind Backquote default.
 */
export const getDictationHotkey = (): string[] => {
  const platform = getUserPlatform();
  const layout = getKeyboardLayout();
  const layoutDefaults: Record<DictationHotkeyPlatform, readonly string[]> = {
    ...DICTATION_HOTKEY_DEFAULTS,
    web: resolveWebDefault(layout, isMac),
  };
  if (!isDictationHotkeySettingsEnabled()) {
    return [...layoutDefaults[platform]];
  }
  const field = PLATFORM_HOTKEYS_FIELD[platform];
  const user = getCurrentUser();
  const stored = (user?.dictation as Record<string, string[]> | undefined)?.[field];
  return resolveUsableChord(platform, stored, layoutDefaults);
};

export const useDictationHotkey = (): { hotkey: string[] } => {
  const platform = getUserPlatform();
  const field = PLATFORM_HOTKEYS_FIELD[platform];
  const isHotkeySettingsEnabled = useIsDictationHotkeySettings();
  const { data: stored } = useQueryUser((data) => data.dictation?.[field]);
  const { layout } = useKeyboardLayout();

  const layoutDefaults: Record<DictationHotkeyPlatform, readonly string[]> = {
    ...DICTATION_HOTKEY_DEFAULTS,
    web: resolveWebDefault(layout, isMac),
  };

  if (!isHotkeySettingsEnabled) {
    return { hotkey: [...layoutDefaults[platform]] };
  }
  return {
    hotkey: resolveUsableChord(platform, stored, layoutDefaults),
  };
};
